/**
 * Enhanced Experience Strategy - Queue-Driven Version
 * 
 * This strategy creates queue items for experience requirements instead of 
 * using the complex phase-based approach. It integrates with the Central Discovery Queue
 * and creates INLINE_SLIDER template items for experience selection.
 */

import { runAIMatches } from '../api';
import { StrategyPhase } from './baseStrategy';
import centralDiscoveryQueue from '../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../discoveryQueueSchema';

// Clean confirmation message for session restoration
export const restore_message = "Perfect! I've set the experience range for your search. Let's continue with the next topic.";

/**
 * Create single experience queue item with OR-ed boolean string
 * Creates ONE queue item with all possible experience ranges for INLINE_SLIDER template
 * @param {Object} baseRequirement - Base requirement for metadata
 * @param {Object} queries - Queries object for lookups
 * @returns {Object} Single queue item with OR-ed experience ranges
 */
const createExperienceQueueItem = (baseRequirement, queries = {}) => {
  // Parse existing experience range if available for default selection
  let defaultRange = [2, 15];
  if (baseRequirement.bool_str) {
    const match = baseRequirement.bool_str.match(/workexp\.overall_experience:(\d+)-(\d+)/);
    if (match && match.length >= 3) {
      const minMonths = parseInt(match[1]);
      const maxMonths = parseInt(match[2]);
      const minYears = Math.round(minMonths / 12);
      const maxYears = Math.round(maxMonths / 12);
      defaultRange = [minYears, maxYears];
    }
  }
  
  // Create OR-ed boolean string with all possible experience ranges (0-30 years in 1-year increments)
  const experienceRanges = [];
  for (let i = 0; i < 30; i++) {
    const startMonth = i * 12;
    const endMonth = (i + 1) * 12;
    experienceRanges.push(`(workexp.overall_experience:${startMonth}-${endMonth})`);
  }
  
  // Join all ranges with OR operator
  const orBoolStr = `((${experienceRanges.join(' OR ')}))`;
  
  const queueId = `queue_experience_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const queueItem = {
    // Core fields expected by UI
    queue_id: queueId,
    section_id: baseRequirement.section_id || `experience_${Date.now()}`,
    section_name: 'Overall Years of Experience',
    section_type: baseRequirement.section_type || 'Must Have',
    section_order: baseRequirement.section_order || 0,
    bool_str: orBoolStr,
    topic: 'experience',
    disco_done: false,
    
    // Template configuration for INLINE_SLIDER
    template_type: DISCOVERY_TEMPLATE_TYPES.INLINE_SLIDER,
    ai_intro_message: 'Based on your search, here\'s how your current employee base is distributed by years of experience. Select a range to refine your candidate search.',
    ux_config: {
      title: '',
      chart_title: 'Years of Experience',
      chart_subtitle: 'Drag the sliders to set your preferred experience range',
      confirm_button_text: 'Confirm Experience Range',
      ro_footer: false
    },
    template_props: {
      value: defaultRange,
      min: 0,
      max: 30,
      candidateCount: 12000, // Default count, will be updated by template with real data
      showSparkline: true,
      unit: 'years',
      // Agentic flow props
      enableAgenticToggle: true,
      topic: 'experience',
      agenticContext: `Experience range selection context. Current range: ${defaultRange[0]}-${defaultRange[1]} years. Please select an optimal experience range (0-30 years) that balances candidate pool size with role relevance.`,
      sessionId: centralDiscoveryQueue.sessionId || 'default-session'
    },
    
    // Timestamps
    created_at: Date.now(),
    updated_at: Date.now()
  };
  
  return queueItem;
};

/**
 * Enhanced experience strategy function
 * @param {Object} decision - Current decision object
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @returns {Object} Updated decision object
 */
const enhancedExperienceStrategy = async (decision, messages, setMessages) => {
  console.log('🎯🎯🎯 ENHANCED EXPERIENCE STRATEGY CALLED!!! - Processing experience topic');
  console.log('SESSION ID:', decision.sessionId);
  console.log('CURRENT SESSION ID:', decision.currentSessionId);
  console.log('🎯 ENHANCED EXPERIENCE STRATEGY - decision object:', {
    sessionId: decision.sessionId,
    currentSessionId: decision.currentSessionId,
    userId: decision.userId,
    isJdUrl: decision.isJdUrl,
    getAllRequirements: !!decision.getAllRequirements,
    allDecisionProps: Object.keys(decision)
  });
  
  try {
    // Step 1: Get requirements (from JD URL or AI matches)
    let aiMatchesResult;
    const existingReqs = decision.getAllRequirements ? decision.getAllRequirements() : [];
    
    if (decision.isJdUrl) {
      // Use existing requirements from JD URL
      aiMatchesResult = {
        search_gen_response: {
          icp_requirements: existingReqs
        }
      };
    } else {
      // Run AI matches to get current requirements
      aiMatchesResult = await runAIMatches(messages);
      if (!aiMatchesResult) {
        throw new Error('Failed to get AI matches');
      }
    }
    
    // Step 2: Get requirements and filter for experience
    const icpRequirements = aiMatchesResult.search_gen_response?.icp_requirements || [];
    const queries = decision.queries || {};
    
    const experienceRequirements = icpRequirements.filter(req => 
      req.section_name === "Overall Years of Experience" || 
      (req.bool_str && req.bool_str.includes('workexp.overall_experience'))
    );
    
    console.log('🎯 ENHANCED EXPERIENCE STRATEGY: Found experience requirements:', experienceRequirements.length);
    
    // Step 3: Create queue items for experience requirements
    // Always create at least one experience queue item, even if no requirements found
    const requirementsToProcess = experienceRequirements.length > 0 ? experienceRequirements : [{
      section_id: `experience_default_${Date.now()}`,
      section_name: "Overall Years of Experience",
      section_type: "Must Have",
      section_order: 1,
      bool_str: "(workexp.overall_experience:24-180)" // Default 2-15 years
    }];
    
    // Always create experience queue items, even if no requirements found
    console.log('🎯 ENHANCED EXPERIENCE STRATEGY: Processing experience requirements');
    
    // 🎯 CRITICAL: Check if central discovery queue is properly initialized (like company_stage strategy)
    if (!centralDiscoveryQueue.sessionId) {
      console.error('❌ ENHANCED EXPERIENCE STRATEGY: Central discovery queue not initialized - no session ID');
      throw new Error('Central discovery queue not initialized - no session ID');
    }
    
    console.log('✅ ENHANCED EXPERIENCE STRATEGY: Central discovery queue is initialized with session ID:', centralDiscoveryQueue.sessionId);
    
    // Step 3.5: Check for existing experience queue items to prevent duplicates
    const existingExperienceItems = centralDiscoveryQueue.getItemsByTopic('experience');
    console.log('🔍 ENHANCED EXPERIENCE STRATEGY: Found existing experience queue items:', existingExperienceItems.length);
    
    if (existingExperienceItems.length > 0) {
      console.log('🔄 ENHANCED EXPERIENCE STRATEGY: Experience queue items already exist, updating with latest data');
      
      // Update the first existing experience item with latest data
      const existingItem = existingExperienceItems[0];
      const baseRequirement = requirementsToProcess[0];
      
      try {
        const updatedQueueItem = createExperienceQueueItem(baseRequirement, queries);
        
        // Preserve original queue_id and timestamps, but update content
        updatedQueueItem.queue_id = existingItem.queue_id;
        updatedQueueItem.created_at = existingItem.created_at;
        updatedQueueItem.updated_at = Date.now();
        
        // Override the existing item in central queue
        centralDiscoveryQueue.queue.set(existingItem.queue_id, updatedQueueItem);
        centralDiscoveryQueue.notifyListeners();
        
        console.log('✅ Updated existing experience queue item with latest range data');
      } catch (error) {
        console.error('❌ Error updating experience queue item:', error);
      }
      
      // Still update legacy requirements for backward compatibility
      if (decision.updateRequirements && requirementsToProcess.length > 0) {
        decision.updateRequirements(requirementsToProcess, 'experience');
      }
    } else {
      // Create single experience queue item with OR-ed boolean string
      // Use the first requirement as the base for metadata
      const baseRequirement = requirementsToProcess[0];
      
      try {
        const experienceQueueItem = createExperienceQueueItem(baseRequirement, queries);
        
        // Add the single experience item to the queue
        await centralDiscoveryQueue.addQueueItem(experienceQueueItem);
        
        console.log('✅ Added 1 experience queue item with OR-ed ranges');
      } catch (error) {
        console.error(`❌ Error creating experience queue items:`, error);
      }
    }
    
    console.log('🎯 ENHANCED EXPERIENCE STRATEGY: Experience queue populated with', 
      centralDiscoveryQueue.getAllItems().filter(item => item.topic === 'experience').length, 'items');
    
    // Step 4: Update legacy requirements for backward compatibility
    if (requirementsToProcess.length > 0 && decision.updateRequirements) {
      decision.updateRequirements(requirementsToProcess, 'experience');
    }
    
    // Step 5: No need to refresh results here - only refresh when user confirms items
    
    // Step 6: Return success - queue items will be rendered by ChatInterfaceQueueDrivenSimple
    return {
      ...decision,
      actionType: 'QUEUE_POPULATED',
      strategyPhase: StrategyPhase.CONVERSATION,
      queueItemsCreated: requirementsToProcess.length
    };
    
  } catch (error) {
    console.error('❌ Error in enhanced experience strategy:', error);
    
    // Fall back to regular conversation
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: 'Let\'s discuss the experience requirements for this role. What level of experience are you looking for?',
      strategyPhase: StrategyPhase.CONVERSATION
    };
  }
};

export default enhancedExperienceStrategy;